import {pluralize} from 'lib/stringUtils';

/**
 * Proficiency metrics count (student, skill) pairs, so a raw count only says something
 * once the scope pins one side of the pair. The pinned side names the unit: pin the
 * student and the count is skills, pin the skill and it is students. Pinning both leaves
 * a single measurement, and pinning neither leaves percentages with no unit at all.
 */
export type ProficiencyCountUnit = 'skill' | 'student' | 'measurement' | null;

export interface ProficiencyScope {
  /** Exactly one student is in scope. */
  oneStudent: boolean;
  /** Exactly one skill is in scope. */
  oneSkill: boolean;
}

export const getProficiencyCountUnit = ({
  oneStudent,
  oneSkill
}: ProficiencyScope): ProficiencyCountUnit => {
  if (oneStudent && oneSkill) return 'measurement';
  if (oneStudent) return 'skill';
  if (oneSkill) return 'student';
  return null;
};

/** Whether a raw count can be phrased in a unit the reader can act on. */
export const isCountableUnit = (unit: ProficiencyCountUnit): unit is 'skill' | 'student' =>
  unit === 'skill' || unit === 'student';

/**
 * The note under a proficiency bar. The pinned side is the subject of the sentence, so
 * the two units read in different orders.
 */
export const getProficiencyNote = ({
  unit,
  bucketLabel,
  bucketCount,
  total
}: {
  unit: ProficiencyCountUnit;
  bucketLabel: string;
  bucketCount: number;
  total: number;
}): string => {
  if (!isCountableUnit(unit)) return '';
  const scope = `${bucketCount} of ${total}`;
  return unit === 'skill'
    ? `${bucketLabel} at ${scope} ${pluralize('Skill', total)}`
    : `${scope} ${pluralize('student', total)} at ${bucketLabel}`;
};

/** The parenthetical beside a bucket's percentage, e.g. `(2 skills)`. */
export const getProficiencyCountAnnotation = (unit: ProficiencyCountUnit, count: number): string =>
  isCountableUnit(unit) ? `(${count} ${pluralize(unit, count)})` : '';
